import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@dopomogai/ui';
import { CheckCircle2, Users, Zap, Globe, ShieldCheck, Server, Brain, Layout, Map, Key, Database, Lock, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

export type PricingTier = {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: { label: string; actionId: string };
  highlight?: boolean;
  teamSize: number;
};

export type UnitPricingSectionProps = {
  titleKey?: string;
  subtitleKey?: string;
  tiers: PricingTier[];
  onAction?: (actionId: string) => void;
};

export const UnitPricingSection: React.FC<UnitPricingSectionProps> = ({
  titleKey,
  subtitleKey,
  tiers,
  onAction
}) => {
  const { t } = useTranslation('homepage');

  // Hardcoded Logic for tiers if passed from CMS
  const refinedTiers = tiers.map(t => {
    if (t.name.includes("Core")) {
      return {
        ...t,
        features: [
          "1 Architect (Strategy & Logic)",
          "1 Builder (Integrations & Code)",
          "1 Data Engineer (Context & Graph)",
          "Full 'Canvas OS' Setup",
          "You Own the Asset (Data & Logic)"
        ]
      };
    }
    return t;
  });

  return (
    <section className="bg-white py-20 md:py-32 relative overflow-hidden border-t-2 border-slate-100">
      
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-emerald-50/50 blur-[100px] rounded-full -z-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* HEADER SECTION */}
        <div className="text-center mb-20 relative">
          
          <motion.div 
            className="relative mx-auto w-72 md:w-96 mb-10 z-10 bg-white border-2 border-black rounded-2xl overflow-hidden shadow-[6px_6px_0_0_rgba(0,0,0,1)]"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
             {/* The Image fits well: it shows a team building a machine, implying "Engineering Unit" */}
             <img 
               src="/images/illustrations/the-unit-no-text-2k.avif" 
               alt="The Engineering Unit" 
               className="w-full h-auto" 
             />
          </motion.div>

          <Badge text="Managed Engineering" accentColor="emerald" className="mb-4" />
          
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            {t(titleKey || 'pricing.title', 'Hire a Unit, Not a Freelancer.')}
          </h2>
          
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 font-medium leading-relaxed">
            {t(subtitleKey || 'pricing.subtitle', 'We provide a dedicated, managed Engineering Unit to build your AI infrastructure. You get the output of a full squad for the cost of a single senior hire.')}
          </p>

          {/* --- THE PROTOCOL (TIMELINE) --- */}
          {/* VISUAL FIX: Added mt-12 and removed overflow-hidden to prevent badge clipping */}
          <div className="max-w-5xl mx-auto bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 md:p-12 relative mt-16 shadow-sm">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-4 border-white shadow-lg z-20">
                  The Engagement Protocol
              </div>

              {/* Connecting Line (Desktop) */}
              <div className="hidden md:block absolute top-[45%] left-[15%] right-[15%] h-0.5 bg-slate-200 -z-0" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 relative z-10">
                  
                  {/* STEP 1 */}
                  <div className="flex flex-col items-center text-center group">
                      <div className="w-16 h-16 bg-white border-2 border-slate-300 rounded-2xl flex items-center justify-center mb-5 shadow-sm text-slate-600">
                          <Server size={28} />
                      </div>
                      <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">Phase 1 (One-Time)</div>
                      <h4 className="font-black text-slate-900 text-lg mb-2">The Setup Sprint</h4>
                      <p className="text-sm text-slate-600 leading-relaxed px-2">
                          We provision your Private Infrastructure, ingest your Data and build the transformation Roadmap.
                      </p>
                  </div>

                  {/* STEP 2 */}
                  <div className="flex flex-col items-center text-center group">
                      <div className="w-16 h-16 bg-slate-900 border-2 border-black rounded-2xl flex items-center justify-center mb-5 shadow-lg text-emerald-400">
                          <Users size={28} />
                      </div>
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phase 2 (Monthly)</div>
                      <h4 className="font-black text-slate-900 text-lg mb-2">The Engineering Unit</h4>
                      <p className="text-sm text-slate-600 leading-relaxed px-2">
                          We deploy the squad to build, tune, and operate your AI workforce based on the roadmap.
                      </p>
                  </div>

                  {/* STEP 3 */}
                  <div className="flex flex-col items-center text-center group">
                      <div className="w-16 h-16 bg-white border-2 border-emerald-500 rounded-2xl flex items-center justify-center mb-5 shadow-sm text-emerald-700">
                          <ShieldCheck size={28} />
                      </div>
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phase 3 (Perpetual)</div>
                      <h4 className="font-black text-slate-900 text-lg mb-2">Asset Ownership</h4>
                      <p className="text-sm text-slate-600 leading-relaxed px-2">
                          Once built, the machine is yours. You own the Data, Logic, and Config. Pause anytime.
                      </p>
                  </div>

              </div>
          </div>
        </div>

        {/* --- SPACER FOR VISUAL CLARITY --- */}
        <div className="h-12 md:h-24"></div>

        {/* --- PHASE 1: SETUP SPRINT DETAIL BLOCK --- */}
        <div className="max-w-5xl mx-auto mb-24">
            <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-white relative shadow-2xl border-4 border-slate-100">
                {/* Decoration */}
                <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                
                <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start relative z-10">
                    <div className="flex-1">
                        <div className="inline-block bg-emerald-500 text-slate-900 text-xs font-black uppercase tracking-wider px-3 py-1 rounded mb-6">
                            Phase 1
                        </div>
                        <h3 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">The Setup Sprint</h3>
                        <p className="text-slate-400 font-medium text-lg mb-8 leading-relaxed">
                            Before we build agents, we build the house they live in. This is the foundation of your AI-First Company.
                        </p>
                        <div className="flex items-baseline gap-3 mb-2">
                            <span className="text-5xl font-black text-white">$6,000</span>
                            <span className="text-emerald-400 font-bold tracking-tight">One-Time Fee</span>
                        </div>
                        <div className="text-sm text-slate-500 font-mono">Duration: 3 Weeks</div>
                    </div>

                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
                        <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
                            <div className="flex items-center gap-2 mb-3 text-emerald-400 font-bold">
                                <Server size={20} /> <span>Private Infrastructure</span>
                            </div>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Single-Tenant VPC/Droplet + Secure Secrets Vault. Air-gapped environment.
                            </p>
                        </div>
                        <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
                            <div className="flex items-center gap-2 mb-3 text-emerald-400 font-bold">
                                <Brain size={20} /> <span>The Brain</span>
                            </div>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Structured Knowledge Graph ingested from your Repos, Slack, and Docs.
                            </p>
                        </div>
                        <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
                            <div className="flex items-center gap-2 mb-3 text-emerald-400 font-bold">
                                <Layout size={20} /> <span>The Platform</span>
                            </div>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Installation of Canvas OS Kernel, Agents Builder, and Interface Engine.
                            </p>
                        </div>
                        <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700 hover:border-emerald-500/30 transition-colors">
                            <div className="flex items-center gap-2 mb-3 text-emerald-400 font-bold">
                                <Map size={20} /> <span>The Roadmap</span>
                            </div>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                A strategic "Gap Report" identifying toil and a 12-month automation plan.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* --- PHASE 2: UNIT PRICING CARDS --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start mb-24">
          {refinedTiers.map((tier, i) => (
            <div 
              key={i}
              className={clsx(
                "relative flex flex-col p-6 md:p-8 rounded-2xl bg-white transition-all duration-200 h-full",
                tier.highlight 
                  ? "border-4 border-black shadow-[8px_8px_0_0_#10b981] scale-100 md:scale-105 z-10" 
                  : "border-2 border-slate-200 shadow-sm hover:border-black hover:shadow-[4px_4px_0_0_rgba(0,0,0,0.1)]"
              )}
            >
              {/* VISUAL FIX: Added margin-top to badge to prevent clipping if parent has constraints */}
              {tier.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white border-2 border-black px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-sm whitespace-nowrap z-20">
                  Phase 2: Execution
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-2xl font-extrabold text-slate-900">{tier.name}</h3>
                <p className="text-sm font-medium text-slate-500 mt-2 min-h-[40px] leading-relaxed">
                    {tier.description}
                </p>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-100">
                <span className={clsx("text-4xl font-black", tier.highlight ? "text-emerald-600" : "text-slate-900")}>
                  {tier.price}
                </span>
              </div>

              {/* Team Size Badge */}
              <div className="mb-6 p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-3">
                 <div className="flex -space-x-2 shrink-0">
                    {Array.from({ length: tier.teamSize }).map((_, idx) => (
                        <div key={idx} className={clsx(
                            "w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold shadow-sm",
                            tier.highlight ? "bg-emerald-500" : "bg-slate-400"
                        )}>
                            <Users size={14} />
                        </div>
                    ))}
                 </div>
                 <div className="flex flex-col">
                    <span className="text-xs font-bold text-slate-900 leading-tight">
                        {tier.teamSize === 1 ? '1 Dedicated Engineer' : `${tier.teamSize} Dedicated Engineers`}
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">Fully Managed & Trained</span>
                 </div>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {tier.features.map((feat, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 font-medium">
                    <CheckCircle2 size={18} className={clsx("mt-0.5 shrink-0", tier.highlight ? "text-emerald-500" : "text-slate-400")} />
                    {feat}
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Button 
                  variant={tier.highlight ? 'primary' : 'outline'} 
                  className={clsx("w-full py-4 text-base", tier.highlight && "bg-emerald-500 hover:bg-emerald-400 border-black")}
                  text={tier.cta.label}
                  onClick={() => onAction?.('SCROLL_BOOKING')}
                />
                <p className="text-center text-[10px] text-slate-400 mt-3 font-medium">
                   {tier.highlight ? "3 Month Foundation Commitment" : "Cancel anytime after Foundation Build"}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* --- PHASE 3: THE SOVEREIGN HANDOVER --- */}
        {/* VISUAL FIX: Added mt-12 to the container to ensure the badge floats freely */}
        <div className="max-w-5xl mx-auto mb-20 relative mt-16">
            <div className="bg-white rounded-2xl p-8 md:p-12 border-2 border-emerald-100 shadow-xl relative group hover:border-emerald-300 transition-colors">
                
                {/* Badge */}
                <div className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-1/2 bg-white text-emerald-700 border border-emerald-200 px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-md z-20">
                    Phase 3: Graduation
                </div>

                <div className="flex flex-col md:flex-row items-center gap-10 text-center md:text-left">
                    <div className="shrink-0 bg-emerald-50 p-8 rounded-full border border-emerald-100 shadow-inner">
                        <ShieldCheck size={56} className="text-emerald-500" />
                    </div>
                    
                    <div className="flex-1">
                        <h3 className="text-2xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight">The Sovereign Handover</h3>
                        <p className="text-slate-600 text-lg leading-relaxed max-w-2xl">
                            Our goal is not to rent you software forever. Our goal is to build you an asset and leave. 
                            When you are ready, you take the keys.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 w-full md:w-auto min-w-[240px]">
                        <div className="flex items-center gap-3 text-sm font-bold text-slate-700 bg-slate-50 px-5 py-3 rounded-lg border border-slate-100 hover:bg-white hover:shadow-md transition-all">
                            <Key size={18} className="text-emerald-500" />
                            <span>Perpetual License</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm font-bold text-slate-700 bg-slate-50 px-5 py-3 rounded-lg border border-slate-100 hover:bg-white hover:shadow-md transition-all">
                            <Database size={18} className="text-emerald-500" />
                            <span>You Keep The Data</span>
                        </div>
                         <div className="flex items-center gap-3 text-sm font-bold text-slate-700 bg-slate-50 px-5 py-3 rounded-lg border border-slate-100 hover:bg-white hover:shadow-md transition-all">
                            <Wrench size={18} className="text-emerald-500" />
                            <span>Maintenance Ready</span>
                        </div>
                       
                    </div>
                </div>
            </div>
        </div>

        {/* FOOTER BADGES */}
        <div className="mt-24 border-t border-slate-200 pt-10 flex flex-wrap justify-center gap-x-16 gap-y-6 opacity-80">
           <div className="flex items-center gap-2 text-slate-600 font-bold text-sm">
              <Globe size={18} className="text-emerald-600" /> 100% Remote / Async
           </div>
           <div className="flex items-center gap-2 text-slate-600 font-bold text-sm">
              <ShieldCheck size={18} className="text-emerald-600" /> Mutual NDA
           </div>
           <div className="flex items-center gap-2 text-slate-600 font-bold text-sm">
              <Zap size={18} className="text-emerald-600" /> 7-Day Replacement Policy
           </div>
        </div>

      </div>
    </section>
  );
};